import isTizen from '@accedo/xdk-device-samsung-tizen/esm/detection'
import { Router } from '@lightningjs/sdk'
import { BASE_ROUTES, FIRST_LANDING, FORGOT_PASSWORD, LANDING, LOGIN } from '../../constants/routes'
import { AppConfig } from '../../services/appConfig'
import { getRefreshToken, storeUserData, updateUserSubscriptionData } from '../authentication'
import { captureSessionData } from '../segmentAnalytics'
import FloVizbeeConverters from './FloVizbeeConverters'

export default class FloVizbeeHandlers {
  /**
   * This handler can be used to handle start video deeplink events
   * received from the mobile device.
   *
   * @param { vizbee.continuity.messages.VideoInfo } videoInfo
   *              - guid (string) video content id
   *              - title (string) video content title
   *              - imgUrl (string) video content imageUrl
   *              - isLive (boolean) video content type (LIVE or VOD)
   *              - videoUrl (string) video content url
   *              - startTime (number video resume time.
   *              - subtitle (string) video content subtitle - optional
   *              - desc (string) video content description - optional
   */
  static async deeplinkHandler(videoInfo) {
    // 1. Convert Vizbee VideoInfo to your internal video data structure
    const playerModel = FloVizbeeConverters.fromVideoInfo(videoInfo)

    // 2. Check if the user is authenticated and log it in, if necessary
    const tvRefreshToken = getRefreshToken()
    const { refreshToken: mobileRefreshToken } = videoInfo.customStreamInfo

    if (!tvRefreshToken && mobileRefreshToken) {
      const data = await AppConfig.shared.authService.refreshToken({ token: mobileRefreshToken })

      if (data?.token && data?.refresh_token) {
        storeUserData(data)
        await updateUserSubscriptionData()

        playerModel.authDeeplinked = true

        if (isTizen) {
          await AppConfig.shared.samsungCWService.restoreContinueWatching()
        }

        void captureSessionData()
      }
    }

    // 3. Handles the specific case where the TV app is unauthenticated, then by casting a video into it it automatically
    // logs in and then the user decides to player another video without the previous one finishing. By this moment the user is
    // already authenticated, but we don't want to go to the previous screen if it was the Landing or any screen from the flow where the
    // user has not signed in just yet. This ensures they get redirected to Home instead.
    if (!playerModel.authDeeplinked && tvRefreshToken) {
      const history = Router.getHistory()
      const previousScreen = history[history.length - 1]
      const previousScreenHash = previousScreen?.hash
      const unauthenticatedRoutes = [FIRST_LANDING, LANDING, LOGIN, FORGOT_PASSWORD]
      const wasInUnauthenticatedRoute = unauthenticatedRoutes.some((route) =>
        route.includes(previousScreenHash),
      )
      playerModel.authDeeplinked = wasInUnauthenticatedRoute
    }

    // 4. Deeplink and play this video at the right resume position
    Router.navigate(`${BASE_ROUTES.PLAYER}/${playerModel.id}`, playerModel)
  }

  /**
   * Logins the user whenever they tap on the cast icon on the mobile app,
   * but they don't cast any video.
   *
   * @param { vizbee.continuity.messages.VizbeeEvent } signInEvent
   *              - type (string) event type = "tv.vizbee.homesign.signin"
   *              - data (object) object that has signin event data
   */
  static async signInHandler(signInEvent) {
    if (signInEvent?.data) {
      const userAuthInfo = signInEvent.data.authInfo

      if (userAuthInfo?.refreshToken) {
        const tvRefreshToken = getRefreshToken()
        const mobileRefreshToken = userAuthInfo.refreshToken

        // If user is already authenticated, simply ignore the login process
        if (!tvRefreshToken && mobileRefreshToken) {
          const data = await AppConfig.shared.authService.refreshToken({
            token: mobileRefreshToken,
          })

          if (data?.token && data?.refresh_token) {
            storeUserData(data)
            await updateUserSubscriptionData()

            if (isTizen) {
              await AppConfig.shared.samsungCWService.restoreContinueWatching()
            }

            void captureSessionData()

            const isCurrentRoutePlayer = Router.getActiveRoute()?.includes(BASE_ROUTES.PLAYER)

            if (!isCurrentRoutePlayer) {
              Router.navigate(BASE_ROUTES.HOME)
            }
          }
        }
      }
    }
  }
}
